import { Paper } from '@mui/material';
import * as React from 'react';
import { API_SERVER } from 'modules/common/api';
import LoadingIcon from 'modules/common/component/LoadingIcon';
import { SYSTEM_CATEGORY_SCOPE, SYSTEM_CATEGORY_TYPE, some } from 'modules/common/constants';
import useGeneralHook from 'modules/common/hook/useGeneralHook';
import { setLoading } from 'modules/common/redux/commonReducer';
import HealthcareServiceCategoryContent from '../component/exam/HealthcareServiceCategoryContent';
import PriceListFormDialog from '../component/exam/PriceListFormDialog';
import TabCategory from '../component/exam/TabCategory';
import { axiosThunk } from 'modules/common/redux/axios';
import { useDeleteMutate, useFetch } from 'modules/common/hook';

interface Props {}

const HealthcareServiceAdminPage: React.FunctionComponent<Props> = (props) => {
  const { dispatch, openSnackbar, intl } = useGeneralHook();
  const [categoryListFormData, setCategoryListFormData] = React.useState<some | undefined>();
  const [categoryId, setCategoryId] = React.useState<number | undefined>();

  const {
    data: categoryList,
    revalidate,
    isValidating,
  } = useFetch(
    API_SERVER.systemCategory.getList({
      scope: SYSTEM_CATEGORY_SCOPE.healthCheck,
      type: SYSTEM_CATEGORY_TYPE.priceList,
    }),
  );

  const category = categoryList?.find((v) => v.id === categoryId);

  const onSubmitCategoryListForm = React.useCallback(
    async (value: some) => {
      const data = {
        ...value,
        type: SYSTEM_CATEGORY_TYPE.priceList,
        scope: SYSTEM_CATEGORY_SCOPE.healthCheck,
      };
      try {
        dispatch(setLoading(true));
        if (value.id) {
          await dispatch(axiosThunk({ url: API_SERVER.systemCategory.update(value.id), method: 'put', data }));
        } else {
          await dispatch(axiosThunk({ url: API_SERVER.systemCategory.create(), method: 'post', data }));
        }
        revalidate();
        openSnackbar({ message: intl.formatMessage({ id: 'updateSuccess' }) });
      } catch (e: any) {
        const errors = e?.response?.data?.errors;
        if (errors) {
          errors?.forEach((v) => {
            openSnackbar({ message: v.message, type: 'error' });
          });
        } else {
          openSnackbar({ message: intl.formatMessage({ id: 'updateFail' }), type: 'error' });
        }
      } finally {
        dispatch(setLoading(false));
        setCategoryListFormData(undefined);
      }
    },
    [dispatch, intl, openSnackbar, revalidate],
  );

  const onDeleteMutate = useDeleteMutate({
    onSuccess: () => {
      revalidate();
      setCategoryListFormData(undefined);
    },
  });
  const onDeleteCategory = React.useCallback(
    async (value: some) => {
      onDeleteMutate(
        {
          url: API_SERVER.systemCategory.delete(value.id),
          method: 'delete',
        },
        { confirmMessage: intl.formatMessage({ id: 'confirmDeleteServicePrice' }, { name: value.name }) },
      );
    },
    [intl, onDeleteMutate],
  );

  React.useEffect(() => {
    if (!categoryId && categoryList && categoryList?.length > 0) {
      setCategoryId(categoryList?.[0]?.id);
    }
  }, [categoryId, categoryList]);

  return (
    <Paper style={{ flex: 1, overflow: 'auto' }}>
      <TabCategory
        data={categoryList}
        onCreateOrUpdateCategory={setCategoryListFormData}
        onDeleteCategory={onDeleteCategory}
        categoryId={categoryId}
        setCategoryId={setCategoryId}
      />
      {!categoryList && isValidating && <LoadingIcon />}
      {category && <HealthcareServiceCategoryContent category={category} />}
      <PriceListFormDialog
        open={!!categoryListFormData}
        formData={categoryListFormData}
        onSubmit={onSubmitCategoryListForm}
        onClose={() => {
          setCategoryListFormData(undefined);
        }}
        categoryList={categoryList}
      />
    </Paper>
  );
};

export default HealthcareServiceAdminPage;
